import React, { useCallback } from 'react';

import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { isEqual } from 'lodash';
import { Moment } from 'moment';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  TraitementResponseDtoListStatutEnum,
} from 'lib_api/lib/api/gen';

import { FilterButton } from 'components/WrappedComponents';
import DashboardSearchBarWithTooltip from 'components/WrappedComponents/SearchBar/DashboardSearchBarWithTooltip';
import { useAFFilterContext } from 'hooks/AFFilterStoreContext';
import { StatutTraitementFacturation } from 'hooks/dossiers/useSearchDossierSummaries';
import {
  FilterDashboardAutoriteFourriere,
  FormFilterAutoriteFourriere,
} from 'pages/autorite-fourriere/Dashboard/DashboardHeader/Filters/types';

import FilterFormAutoriteFourriere from './FilterFormAutoriteFourriere';

import 'styles/DashboardHeaderFilter.less';

interface FiltersProps {
  filter: Partial<FilterDashboardAutoriteFourriere>;
  setFilter: (value: Partial<FilterDashboardAutoriteFourriere>) => void;
}

function keepDossierSortis(
  statut: TraitementResponseDtoListStatutEnum[],
  sortieFilter: boolean,
  dateRangeSortieDefinitive: [Moment | undefined, Moment | undefined],
  statutsTraitementFacturation: StatutTraitementFacturation[],
  motifDeSortieFilter: string[],
  includeDossiersAnonymises: boolean,
  includeOnlyNotDetruitApresSorti: boolean,
): boolean {
  // automatically check the "sorti" checkbox
  return (
    // if the statut SORTI is selected
    statut.includes(TraitementResponseDtoListStatutEnum.SORTI) ||
    // if the checkbox is checked
    (statut.length === 0 && sortieFilter) ||
    //if we have traitement facturation values we need to include dossier SORTI
    statutsTraitementFacturation?.length > 0 ||
    // if we search for a date sortie
    !!dateRangeSortieDefinitive[0] ||
    !!dateRangeSortieDefinitive[1] ||
    motifDeSortieFilter.length !== 0 ||
    // if we search for anonymized dossiers
    includeDossiersAnonymises === true ||
    includeOnlyNotDetruitApresSorti === true
  );
}

//add sortie status if we have a traitement facturation status and already specified statuses
function computeStatus(
  statut: TraitementResponseDtoListStatutEnum[],
  statutsTraitementFacturation: StatutTraitementFacturation[],
): TraitementResponseDtoListStatutEnum[] | undefined {
  if (
    statutsTraitementFacturation?.length > 0 &&
    statut?.length > 0 &&
    !statut.includes(TraitementResponseDtoListStatutEnum.SORTI)
  ) {
    statut.push(TraitementResponseDtoListStatutEnum.SORTI);
  }

  return statut.length > 0 ? statut : undefined;
}

function Filters({ filter, setFilter }: FiltersProps): React.ReactElement {
  const {
    includeDossiersSortis,
    includeOnlyActionsRequises,
    includeOnlyNonTraiteFacturation,
    search,
    ...formFilter
  } = filter;
  const [, , afActionsRequises] = useAFFilterContext();

  const keepOnlyActionsRequises = useCallback(
    (actionsRequises?: ActionsRequisesDtoActionsRequisesEnum[]): boolean => {
      return isEqual(new Set(actionsRequises), new Set(afActionsRequises));
    },
    [afActionsRequises],
  );

  //keep the checkbox checked only if the traitement facturation statuses contains only NON_TRAITE
  const keepOnlyNonTraiteFacturation = useCallback(
    (statutsTraitementFacturation?: StatutTraitementFacturation[]): boolean => {
      return isEqual(
        new Set(statutsTraitementFacturation),
        new Set(StatutTraitementFacturation.NON_TRAITE),
      );
    },
    [],
  );

  function setFormFilter(value: Partial<FormFilterAutoriteFourriere>) {
    const {
      statut,
      dateRangeSortieDefinitive,
      actionsRequises,
      statutsTraitementFacturation,
      motifDeSortie,
      includeDossiersAnonymises,
      includeOnlyNotDetruitApresSorti,
    } = value;

    setFilter({
      ...filter,
      ...value,
      includeOnlyNotDetruitApresSorti,
      includeDossiersSortis: keepDossierSortis(
        statut ?? [],
        filter.includeDossiersSortis ?? false,
        dateRangeSortieDefinitive ?? [undefined, undefined],
        statutsTraitementFacturation ?? [],
        motifDeSortie ?? [],
        includeDossiersAnonymises ?? false,
        includeOnlyNotDetruitApresSorti ?? false,
      ),
      includeOnlyActionsRequises: keepOnlyActionsRequises(actionsRequises),
      includeOnlyNonTraiteFacturation: keepOnlyNonTraiteFacturation(
        statutsTraitementFacturation ?? [],
      ),
      statut: computeStatus(statut ?? [], statutsTraitementFacturation ?? []),
    });
  }

  function setIncludeDossiersSortis(event: CheckboxChangeEvent) {
    const {
      statut,
      dateRangeSortieDefinitive,
      motifDeSortie,
      includeOnlyNotDetruitApresSorti,
    } = filter;
    const updatedStatut =
      statut?.filter(s => s !== TraitementResponseDtoListStatutEnum.SORTI) ||
      [];

    // Only add the SORTI statut filter if the statut filter is not empty
    if (event.target.checked && (statut?.length ?? -1) > 0) {
      updatedStatut.push(TraitementResponseDtoListStatutEnum.SORTI);
    }

    setFilter({
      ...filter,
      statut: updatedStatut,
      includeDossiersSortis: event.target.checked,
      // clear the date sortie filter when checkbox unchecked
      dateRangeSortieDefinitive: event.target.checked
        ? dateRangeSortieDefinitive
        : undefined,
      motifDeSortie: event.target.checked ? motifDeSortie : [],
      includeDossiersAnonymises: undefined,
      includeOnlyNotDetruitApresSorti: event.target.checked
        ? includeOnlyNotDetruitApresSorti
        : undefined,
    });
  }

  function setIncludeOnlyActionsRequises(event: CheckboxChangeEvent) {
    const isChecked = event.target.checked;
    setFilter({
      ...filter,
      actionsRequises: isChecked ? afActionsRequises : undefined,
      includeOnlyActionsRequises: isChecked,
    });
  }

  function setIncludeOnlyNonTraiteFacturation(event: CheckboxChangeEvent) {
    const isChecked = event.target.checked;
    const { statut } = filter;
    const updatedStatut =
      statut?.filter(s => s !== TraitementResponseDtoListStatutEnum.SORTI) ||
      [];

    // Only add the SORTI statut filter if the statut filter is not empty
    if (event.target.checked && (statut?.length ?? -1) > 0) {
      updatedStatut.push(TraitementResponseDtoListStatutEnum.SORTI);
    }

    if (isChecked) {
      setIncludeDossiersSortis(event);
    }

    setFilter({
      ...filter,
      statut: isChecked ? updatedStatut : filter.statut,
      statutsTraitementFacturation: isChecked
        ? [StatutTraitementFacturation.NON_TRAITE]
        : undefined,
      includeDossiersSortis: isChecked
        ? isChecked
        : filter.includeDossiersSortis,
      includeOnlyNonTraiteFacturation: isChecked,
    });
  }

  return (
    <div className="dashboard-header-filter">
      <DashboardSearchBarWithTooltip
        search={search}
        filter={filter}
        setFilter={setFilter}
      />
      <FilterButton
        setFilter={setFormFilter}
        filter={formFilter}
        textInactiveFilter="Filtres"
        textActiveFilter="Filtres (actifs)"
      >
        {(setData, data) => (
          <FilterFormAutoriteFourriere filter={data} setFilter={setData} />
        )}
      </FilterButton>
      <Checkbox
        checked={includeDossiersSortis}
        onChange={setIncludeDossiersSortis}
      >
        Afficher les véhicules sortis
      </Checkbox>
      <Checkbox
        checked={includeOnlyActionsRequises}
        onChange={setIncludeOnlyActionsRequises}
      >
        Afficher uniquement les actions requises
      </Checkbox>
      <Checkbox
        checked={includeOnlyNonTraiteFacturation}
        onChange={setIncludeOnlyNonTraiteFacturation}
      >
        Afficher uniquement les véhicules non traités en facturation
      </Checkbox>
    </div>
  );
}

export default Filters;
